import { useMemo, useState } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import {
  ThumbsUp,
  ThumbsDown,
  UserCheck,
  ChevronRight,
  ChevronDown,
  CheckCircle2,
  XCircle,
  ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useFeedbackMutations } from '@/hooks/use-feedbacks';
import type { FeedbackItem } from '@/types';

const POSITIVE_TYPES = [
  'intent_matches',
  'tone_was_right',
  'sources_used_were_right'
];

// Display names for known types; unknown ones fall back to a humanised value.
const FEEDBACK_TYPE_LABELS: Record<string, string> = {
  intent_matches: 'Intent matches',
  tone_was_right: 'Tone was right',
  sources_used_were_right: 'Sources were right',
  intent_doesnt_match: "Intent doesn't match",
  tone_was_wrong: 'Tone was wrong',
  sources_used_were_wrong: 'Sources were wrong'
};

function isPositiveFeedback(
  sentiment: string | null,
  feedbackType: string | null
): boolean {
  return (
    sentiment === 'positive' ||
    (feedbackType != null && POSITIVE_TYPES.includes(feedbackType))
  );
}

export function feedbackTypeLabel(feedbackType: string): string {
  const known = FEEDBACK_TYPE_LABELS[feedbackType];
  if (known) return known;
  // Unknown type: "resolution_was_correct" -> "Resolution was correct".
  const words = feedbackType.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function MarkdownBlock({ text }: { text: string }) {
  const html = useMemo(
    () => DOMPurify.sanitize(marked(text, { async: false }) as string),
    [text]
  );
  return (
    <div
      className='prose prose-sm max-w-none text-sm [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:mb-2 [&_ol]:mb-2 [&_li]:my-0.5 [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs [&_pre]:rounded [&_pre]:bg-muted [&_pre]:p-2 [&_a]:text-primary [&_a]:underline'
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function ContextBlock({
  label,
  text
}: {
  label: string;
  text: string | null;
}) {
  return (
    <div className='flex flex-col gap-1.5'>
      <span className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
        {label}
      </span>
      {text ? (
        <MarkdownBlock text={text} />
      ) : (
        <span className='text-sm text-muted-foreground'>—</span>
      )}
    </div>
  );
}

function AddressDialog({
  feedbackId,
  onClose
}: {
  feedbackId: string | null;
  onClose: () => void;
}) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const { markAddressed } = useFeedbackMutations();

  async function handleSubmit() {
    if (!feedbackId || !note.trim()) return;
    setSaving(true);
    try {
      await markAddressed(feedbackId, note);
      setNote('');
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={!!feedbackId}
      onOpenChange={(open) => {
        if (!open) {
          setNote('');
          onClose();
        }
      }}
    >
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Mark as addressed</DialogTitle>
        </DialogHeader>
        <div className='flex flex-col gap-3 py-2'>
          <p className='text-sm text-muted-foreground'>
            Briefly describe how this feedback was addressed.
          </p>
          <Textarea
            placeholder='e.g. Updated the knowledge base with corrected intent definitions'
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            maxLength={1000}
            className='resize-none'
          />
          <p className='text-right text-xs text-muted-foreground'>
            {note.length}/1000
          </p>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!note.trim() || saving}>
            {saving ? 'Saving…' : 'Mark addressed'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FeedbackRow({
  item,
  onMarkAddressed
}: {
  item: FeedbackItem;
  onMarkAddressed: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const { unmarkAddressed } = useFeedbackMutations();
  const positive = isPositiveFeedback(item.sentiment, item.feedbackType);
  const FEEDBACK_LIMIT = 80;

  async function handleUnmark() {
    setClearing(true);
    try {
      await unmarkAddressed(item.id);
    } finally {
      setClearing(false);
    }
  }

  return (
    <>
      <tr
        className='cursor-pointer hover:bg-muted/30'
        onClick={() => setOpen((v) => !v)}
      >
        <td className='w-10 px-4 py-3 align-middle'>
          {positive ? (
            <ThumbsUp className='h-4 w-4 text-green-600' />
          ) : (
            <ThumbsDown className='h-4 w-4 text-red-500' />
          )}
        </td>
        <td className='px-4 py-3 align-middle'>
          <div className='flex flex-wrap gap-1'>
            {(item.feedbackType ?? '')
              .split(', ')
              .filter(Boolean)
              .map((type) => (
                <Badge
                  key={type}
                  variant='outline'
                  className={
                    positive
                      ? 'whitespace-nowrap border-green-200 bg-green-50 text-xs text-green-700'
                      : 'whitespace-nowrap border-red-200 bg-red-50 text-xs text-red-700'
                  }
                >
                  {feedbackTypeLabel(type.trim())}
                </Badge>
              ))}
          </div>
        </td>
        <td className='max-w-xs px-4 py-3 align-middle text-sm'>
          <span className='line-clamp-2 text-muted-foreground'>
            {item.feedback ? (
              item.feedback.length > FEEDBACK_LIMIT ? (
                `${item.feedback.slice(0, FEEDBACK_LIMIT)}…`
              ) : (
                item.feedback
              )
            ) : (
              <span className='italic'>No comment</span>
            )}
          </span>
        </td>
        <td className='px-4 py-3 align-middle text-sm'>
          {item.agentName ? (
            <div>
              <div className='font-medium'>{item.agentName}</div>
              {item.agentEmail && (
                <div className='text-xs text-muted-foreground'>
                  {item.agentEmail}
                </div>
              )}
            </div>
          ) : (
            <span className='text-muted-foreground'>—</span>
          )}
        </td>
        <td className='px-4 py-3 align-middle text-sm'>
          {item.brandName ?? <span className='text-muted-foreground'>—</span>}
        </td>
        <td className='px-4 py-3 align-middle'>
          {item.isAddressed ? (
            <Badge
              variant='outline'
              className='gap-1 whitespace-nowrap border-emerald-200 bg-emerald-50 text-xs text-emerald-700'
            >
              <CheckCircle2 className='h-3 w-3' />
              Addressed
            </Badge>
          ) : (
            <span className='text-xs text-muted-foreground'>—</span>
          )}
        </td>
        <td className='whitespace-nowrap px-4 py-3 align-middle text-sm text-muted-foreground'>
          {formatDate(item.createdAt)}
        </td>
        <td className='w-8 px-3 py-3 align-middle text-muted-foreground'>
          {open ? (
            <ChevronDown className='h-4 w-4' />
          ) : (
            <ChevronRight className='h-4 w-4' />
          )}
        </td>
      </tr>

      {open && (
        <tr className='bg-muted/10'>
          <td colSpan={8} className='px-6 py-5'>
            <div className='flex flex-col gap-5'>
              <div className='flex flex-wrap items-center gap-2'>
                {item.ticketUrl && (
                  <a
                    href={item.ticketUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={(e) => e.stopPropagation()}
                    className='inline-flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium text-primary hover:bg-muted/40'
                  >
                    <ExternalLink className='h-3 w-3' />
                    View ticket
                  </a>
                )}
                {item.isHitl && (
                  <Badge
                    variant='outline'
                    className='gap-1 border-blue-200 bg-blue-50 text-blue-700'
                  >
                    <UserCheck className='h-3 w-3' />
                    HITL response
                  </Badge>
                )}
                {item.isAddressed && item.addressedNote && (
                  <div className='flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800'>
                    <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-emerald-600' />
                    <div>
                      <span className='font-medium'>Addressed: </span>
                      {item.addressedNote}
                      {item.addressedAt && (
                        <span className='ml-2 text-xs text-emerald-600'>
                          {formatDate(item.addressedAt)}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className='grid grid-cols-2 gap-6'>
                <div className='rounded-md border border-border bg-background p-4'>
                  <ContextBlock
                    label='Customer query'
                    text={item.customerQuery}
                  />
                </div>
                <div className='rounded-md border border-border bg-background p-4'>
                  <ContextBlock label='AI response' text={item.aiMessage} />
                </div>
              </div>

              {item.feedback && item.feedback.length > FEEDBACK_LIMIT && (
                <div className='rounded-md border border-border bg-background p-4'>
                  <ContextBlock label="Agent's comment" text={item.feedback} />
                </div>
              )}

              <div
                className='flex gap-2'
                onClick={(e) => e.stopPropagation()}
              >
                {item.isAddressed ? (
                  <Button
                    variant='outline'
                    size='sm'
                    className='gap-1.5 text-muted-foreground'
                    disabled={clearing}
                    onClick={handleUnmark}
                  >
                    <XCircle className='h-3.5 w-3.5' />
                    {clearing ? 'Clearing…' : 'Unmark addressed'}
                  </Button>
                ) : (
                  <Button
                    variant='outline'
                    size='sm'
                    className='gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                    onClick={() => onMarkAddressed(item.id)}
                  >
                    <CheckCircle2 className='h-3.5 w-3.5' />
                    Mark as addressed
                  </Button>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

interface FeedbacksTableProps {
  items: FeedbackItem[];
  total: number;
  isLoading: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function FeedbacksTable({
  items,
  total,
  isLoading,
  page,
  totalPages,
  onPageChange
}: FeedbacksTableProps) {
  const [addressingId, setAddressingId] = useState<string | null>(null);

  return (
    <div className='flex flex-col gap-3'>
      <div className='text-sm text-muted-foreground'>
        {total} feedback{total !== 1 ? 's' : ''}
      </div>
      <div className='overflow-hidden rounded-lg border border-border bg-background'>
        <table className='w-full text-sm'>
          <thead className='bg-muted/40 text-muted-foreground'>
            <tr>
              <th className='w-10 px-4 py-3' />
              <th className='px-4 py-3 text-left font-medium'>Reason</th>
              <th className='px-4 py-3 text-left font-medium'>Agent comment</th>
              <th className='px-4 py-3 text-left font-medium'>Agent</th>
              <th className='px-4 py-3 text-left font-medium'>Brand</th>
              <th className='px-4 py-3 text-left font-medium'>Status</th>
              <th className='px-4 py-3 text-left font-medium'>Date</th>
              <th className='w-8 px-3 py-3' />
            </tr>
          </thead>
          <tbody className='divide-y divide-border'>
            {isLoading ? (
              <tr>
                <td
                  colSpan={8}
                  className='px-4 py-10 text-center text-muted-foreground'
                >
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className='px-4 py-10 text-center text-muted-foreground'
                >
                  No feedbacks found.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <FeedbackRow
                  key={item.id}
                  item={item}
                  onMarkAddressed={setAddressingId}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className='flex items-center justify-end gap-2'>
          <Button
            variant='outline'
            size='sm'
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            Previous
          </Button>
          <span className='text-sm text-muted-foreground'>
            {page} / {totalPages}
          </span>
          <Button
            variant='outline'
            size='sm'
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </Button>
        </div>
      )}

      <AddressDialog
        feedbackId={addressingId}
        onClose={() => setAddressingId(null)}
      />
    </div>
  );
}
